import { useState } from 'react'
import toast from 'react-hot-toast'
import { Download, FileText, Loader2, Paperclip } from 'lucide-react'
import { documentsService } from '@/services/documentsService'
import { occurrencesService } from '@/services/occurrencesService'
import type { ProcessSubjectMessage } from '../../../types/occurrences'

async function downloadAttachedDocument(
  processId: number,
  docId: number,
  fallbackName: string,
): Promise<void> {
  try {
    const { blob, fileName } = await documentsService.getDocumentFile(
      processId,
      docId,
    )
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName || fallbackName || `documento-${docId}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.setTimeout(() => window.URL.revokeObjectURL(url), 5000)
  } catch (err) {
    toast.error(
      err instanceof Error ? err.message : 'Falha ao baixar o documento',
    )
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface MessageAttachmentRowProps {
  messageId: number
  index: number
  attachment: { name: string; size: number; type: string; url?: string }
}

/**
 * Card de anexo de mensagem. A signed URL (`attachment.url`) já é gerada
 * pelo backend com `Content-Disposition: attachment; filename="..."`, então
 * `<a download>` faz o browser baixar (não abrir inline). Se a URL não veio
 * hidratada (race ou expirou), busca uma fresh on-demand antes do click.
 */
export function MessageAttachmentRow({
  messageId,
  index,
  attachment,
}: MessageAttachmentRowProps) {
  const [resolving, setResolving] = useState(false)
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(
    attachment.url ?? null,
  )

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Se já temos URL, deixa o navegador seguir o href com `download`.
    if (resolvedUrl) return
    // Sem URL ainda: previne navegação, resolve, dispara download
    // programaticamente (usuário não precisa clicar de novo).
    e.preventDefault()
    if (resolving) return
    setResolving(true)
    try {
      const url = await occurrencesService.getAttachmentDownloadUrl(
        messageId,
        index,
      )
      setResolvedUrl(url)
      // Cria um <a download> efêmero — usar o href atual no DOM fica
      // dessincronizado em alguns browsers se navegar pelo state.
      const a = document.createElement('a')
      a.href = url
      a.download = attachment.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Falha ao baixar o anexo',
      )
    } finally {
      setResolving(false)
    }
  }

  return (
    <li>
      <a
        href={resolvedUrl ?? '#'}
        download={attachment.name}
        onClick={handleClick}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface px-2 py-1 text-xs text-body/80 transition hover:border-border-strong hover:bg-surface-muted"
        title={`Baixar ${attachment.name} (${formatBytes(attachment.size)})`}
      >
        <Paperclip className="h-3.5 w-3.5 text-muted" />
        <span className="max-w-[260px] truncate">{attachment.name}</span>
        <span className="text-[10px] text-subtle">
          {formatBytes(attachment.size)}
        </span>
        {resolving ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Download className="h-3 w-3 opacity-60" />
        )}
      </a>
    </li>
  )
}

/** Lista de anexos de uma mensagem. */
export function MessageAttachments({ message }: { message: ProcessSubjectMessage }) {
  if (!message.attachments || message.attachments.length === 0) return null
  return (
    <ul className="mt-3 flex flex-wrap gap-1.5">
      {message.attachments.map((att, idx) => (
        <MessageAttachmentRow
          key={`${message.id}-${idx}`}
          messageId={message.id}
          index={idx}
          attachment={att}
        />
      ))}
    </ul>
  )
}

interface AttachedDocumentRowProps {
  processId: number
  doc: {
    id: number
    name: string
    type?: string | null
    base_type?: string | null
  }
}

export function AttachedDocumentRow({ processId, doc }: AttachedDocumentRowProps) {
  const [downloading, setDownloading] = useState(false)
  return (
    <li>
      <button
        type="button"
        disabled={downloading}
        onClick={async () => {
          setDownloading(true)
          try {
            await downloadAttachedDocument(processId, doc.id, doc.name)
          } finally {
            setDownloading(false)
          }
        }}
        title={`Baixar ${doc.name}`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface px-2 py-1 text-xs text-body/80 transition-colors hover:border-border-strong hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60"
      >
        <FileText className="h-3.5 w-3.5 text-muted" />
        <span className="truncate max-w-[260px]">{doc.name}</span>
        {doc.type && (
          <span className="text-[10px] uppercase tracking-wide text-muted">
            {doc.type}
          </span>
        )}
        {downloading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Download className="h-3 w-3 opacity-70" />
        )}
      </button>
    </li>
  )
}
